import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import Seo from "../components/Seo";
import { resolveImageUrl } from "../admin/ImageUpload";
import { useCart } from "../context/CartContext";
import { fmtEur } from "./Boutique";
import { ShoppingBag, ArrowLeft, Loader2, Minus, Plus } from "lucide-react";
import { toast } from "sonner";

const BoutiqueProduit = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [qty, setQty] = useState(1);
  const [imgIdx, setImgIdx] = useState(0);

  useEffect(() => {
    api.get(`/shop/products/${productId}`)
      .then(({ data }) => setP(data))
      .catch(() => toast.error("Produit introuvable"))
      .finally(() => setLoading(false));
  }, [productId]);

  const sizes = useMemo(() => Array.from(new Set((p?.variants || []).map((v) => v.size).filter(Boolean))), [p]);
  const colors = useMemo(() => Array.from(new Set((p?.variants || []).map((v) => v.color).filter(Boolean))), [p]);

  const variant = useMemo(() => {
    if (!p) return null;
    return (p.variants || []).find((v) => (v.size || "") === (size || "") && (v.color || "") === (color || "")) || null;
  }, [p, size, color]);

  useEffect(() => {
    if (!p) return;
    const first = (p.variants || []).find((v) => (v.stock || 0) > 0) || (p.variants || [])[0];
    if (first) { setSize(first.size || ""); setColor(first.color || ""); }
  }, [p]);

  const stock = variant?.stock || 0;

  const add = () => {
    if (!variant) { toast.error("Choisissez une déclinaison"); return; }
    if (stock <= 0) { toast.error("Cette déclinaison est épuisée"); return; }
    addItem(p, variant, qty);
    toast.success("Ajouté au panier");
    navigate("/boutique/panier");
  };

  if (loading) return <div className="py-24 text-center text-stone-400"><Loader2 className="animate-spin inline" size={22} /></div>;
  if (!p) return <div className="py-24 text-center text-stone-500">Produit introuvable. <Link to="/boutique" className="text-deep-rose underline">Retour à la boutique</Link></div>;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12 sm:py-16" data-testid="shop-product-page">
      <Seo title={p.name} description={p.description?.slice(0, 150) || `${p.name} — boutique L'Élan Danse`} />
      <Link to="/boutique" className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-deep-rose mb-8" data-testid="shop-back-link">
        <ArrowLeft size={15} /> Retour à la boutique
      </Link>

      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
        <div>
          <div className="rounded-2xl overflow-hidden bg-stone-100 aspect-[4/5]">
            {(p.images || [])[imgIdx] ? (
              <img src={resolveImageUrl(p.images[imgIdx])} alt={p.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-stone-300"><ShoppingBag size={48} /></div>
            )}
          </div>
          {(p.images || []).length > 1 && (
            <div className="flex gap-2 mt-3">
              {p.images.map((img, i) => (
                <button key={img} onClick={() => setImgIdx(i)} className={`h-16 w-16 rounded-lg overflow-hidden border-2 transition-colors ${i === imgIdx ? "border-deep-rose" : "border-transparent"}`}>
                  <img src={resolveImageUrl(img)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          {p.category ? <p className="text-xs uppercase tracking-[0.25em] text-stone-400 mb-2">{p.category}</p> : null}
          <h1 className="font-serif-display text-3xl sm:text-4xl text-stone-800">{p.name}</h1>
          <p className="text-2xl text-deep-rose mt-3">{fmtEur(p.price)}</p>
          {p.description ? <p className="text-stone-600 mt-5 leading-relaxed whitespace-pre-line text-base">{p.description}</p> : null}

          {sizes.length > 0 && (
            <div className="mt-7">
              <p className="text-xs uppercase tracking-widest text-stone-500 mb-2">Taille</p>
              <div className="flex gap-2 flex-wrap">
                {sizes.map((s) => (
                  <button key={s} onClick={() => setSize(s)} data-testid={`shop-size-${s}`} className={`min-w-[48px] px-3 py-2 rounded-lg text-sm border transition-colors ${size === s ? "border-deep-rose bg-blush-light text-deep-rose" : "border-stone-200 text-stone-600 hover:border-stone-400"}`}>{s}</button>
                ))}
              </div>
            </div>
          )}

          {colors.length > 0 && (
            <div className="mt-5">
              <p className="text-xs uppercase tracking-widest text-stone-500 mb-2">Couleur</p>
              <div className="flex gap-2 flex-wrap">
                {colors.map((c) => (
                  <button key={c} onClick={() => setColor(c)} data-testid={`shop-color-${c}`} className={`px-3 py-2 rounded-lg text-sm border transition-colors ${color === c ? "border-deep-rose bg-blush-light text-deep-rose" : "border-stone-200 text-stone-600 hover:border-stone-400"}`}>{c}</button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 flex items-center gap-4">
            <div className="inline-flex items-center border border-stone-200 rounded-full">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-2.5 text-stone-500 hover:text-deep-rose" data-testid="shop-qty-minus"><Minus size={14} /></button>
              <span className="w-8 text-center text-sm" data-testid="shop-qty">{qty}</span>
              <button onClick={() => setQty((q) => Math.min(stock || 99, q + 1))} className="p-2.5 text-stone-500 hover:text-deep-rose" data-testid="shop-qty-plus"><Plus size={14} /></button>
            </div>
            <span className="text-sm text-stone-500" data-testid="shop-stock">
              {stock > 0 ? `${stock} en stock` : "Épuisé"}
            </span>
          </div>

          <button onClick={add} disabled={stock <= 0} data-testid="shop-add-to-cart" className="mt-7 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-coral text-white px-8 py-3.5 rounded-full tracking-widest uppercase text-sm hover:brightness-105 transition-all disabled:opacity-40">
            <ShoppingBag size={15} /> Ajouter au panier
          </button>
          <p className="text-xs text-stone-500 mt-4">Retrait à l&apos;école aux horaires de cours — aucun frais de port.</p>
        </div>
      </div>
    </div>
  );
};

export default BoutiqueProduit;
